import * as api from "@/api";
import ButtonBlock from "@/components/button-block";
import ImportantTip from "@/components/important-tip";
import LoadingScreen from "@/components/loading-screen";
import Options from "@/components/options";
import OptionsPricePremium from "@/components/options-price-premium";
import PremiumCard from "@/components/premium-card";
import styles from "@/components/premium-page.module.css";
import PrivilegesPopup from "@/components/privileges-popup";
import QrCode from "@/components/qr-code";
import { globals } from "@/globals";
import * as signals from "@/signals";
import {
	AppList,
	PaymentMethod,
	PaymentQuery,
	PremiumApp,
	PremiumInfo,
	PriceItem,
} from "@/types";
import { logOut, showDialog } from "@/utils";
import { faAlipay, faWeixin } from "@fortawesome/free-brands-svg-icons";
import { faTags } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { JSX, useEffect, useRef, useState } from "react";

const CURRENCY = "¥";
const SERVICE_PARAM_TO_PREMIUM_APP = Object.freeze({
	airportal: PremiumApp.AIRPORTAL,
	rthe: PremiumApp.WEB_HOSTING,
});

function PremiumPage(): JSX.Element {
	const {
		data: premiumInfo,
		error: premiumInfoError,
		isLoading: isLoadingPremiumInfo,
	} = useQuery({
		queryFn: () => {
			if (!signals.login.value.username) {
				return null;
			}
			return api.getPremiumInfo(() => {
				void logOut();
			});
		},
		queryKey: ["premiumInfo", signals.login.value.username],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		retry: false,
	});
	const {
		data: expirationTime,
		error: expirationTimeError,
		isFetching: isFetchingExpirationTime,
		refetch: refetchExpirationTime,
	} = useQuery({
		queryFn: () => {
			if (!signals.login.value.username) {
				return null;
			}
			return api.getExpirationTime(() => {
				void logOut();
			});
		},
		queryKey: ["expirationTime", signals.login.value.username],
		refetchOnMount: false,
		refetchOnWindowFocus: false,
		retry: false,
	});

	const error = premiumInfoError || expirationTimeError;
	const isLoading = isLoadingPremiumInfo || isFetchingExpirationTime;
	const main = useRef<HTMLElement>(null);
	const serviceParam = (():
		| keyof typeof SERVICE_PARAM_TO_PREMIUM_APP
		| null => {
		const service = globals.params.service?.toLowerCase();
		if (service && service in SERVICE_PARAM_TO_PREMIUM_APP) {
			return service as keyof typeof SERVICE_PARAM_TO_PREMIUM_APP;
		}
		return null;
	})();

	const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(
		PaymentMethod.ALIPAY,
	);
	const [plan, setPlan] = useState<PriceItem>({});
	const [product, setProduct] = useState<string>(serviceParam || "");
	const [qrCode, setQrCode] = useState<string>("");
	const [qrCodeDescription, setQrCodeDescription] = useState<string>("");
	const [showCashier, setShowCashier] = useState<boolean>(false);

	const getDays = (timestamp?: number): number => {
		if (!timestamp) {
			return 0;
		}
		let days =
			Math.round((timestamp - new Date().getTime() / 1000) / 86400) || 0;
		if (days < 0) {
			days = 0;
		}
		return days;
	};

	const getProductInfo = (
		premiumInfo: AppList<PremiumInfo>,
		product: string,
	): PremiumInfo | null => {
		if (!premiumInfo || !product) {
			return null;
		}
		const productKey = product.toLowerCase() as keyof typeof premiumInfo;
		let productInfo = premiumInfo[productKey];
		if (!productInfo) {
			return null;
		}
		const copyKey = productInfo.copy as keyof typeof premiumInfo;
		if (copyKey && copyKey in premiumInfo) {
			productInfo = premiumInfo[copyKey];
			if (!productInfo) {
				return null;
			}
		}
		return productInfo;
	};

	const handleOptionChange = <T,>(
		callback: (newValue: T) => void,
	): ((newValue: T) => void) => {
		return (newValue: T) => {
			callback(newValue);
			hideCashier();
		};
	};

	const handlePaidClick = (): void => {
		void showDialog(t("thankForPurchasing"));
		paid();
	};

	const handlePlaceOrderClick = (): void => {
		void placeOrder();
	};

	const hideCashier = (): void => {
		setQrCode("");
		setShowCashier(false);
	};

	const paid = (): void => {
		if (main.current) {
			main.current.scrollTop = 0;
		}
		void refetchExpirationTime();
		hideCashier();
	};

	const placeOrder = async (): Promise<void> => {
		if (!plan.price || !product) {
			void showDialog(t("premiumSelectProductAndPlan"));
			return;
		}
		const query: PaymentQuery = {
			appName: product,
		};
		query.amount = plan.price * 100;
		const methodInfo =
			globals.methodMap[paymentMethod as keyof typeof globals.methodMap];
		if (!methodInfo) {
			return;
		}
		setQrCodeDescription(
			paymentMethod === PaymentMethod.WECHAT_PAY
				? t("useWechatScanAndPay")
				: "",
		);
		signals.isLoadingScreenShown.value = true;
		try {
			const data = await api.pay(query, paymentMethod);
			void methodInfo.callback({
				setQrCode,
				setShowCashier,
				url: data.code_url,
			});
		} catch (error) {
			api.handleApiError(error);
		} finally {
			signals.isLoadingScreenShown.value = false;
		}
	};

	const updateProduct = (newValue: string): void => {
		setProduct(newValue);
	};

	const productInfo = premiumInfo
		? getProductInfo(premiumInfo, product)
		: null;
	const promotion = productInfo?.promotionText;

	const cards: PremiumInfo[] = [
		{
			appName: PremiumApp.AIRPORTAL,
			data: expirationTime?.airportal?.data || 0,
			explanation: premiumInfo?.airportal?.explanation || "",
			link: "https://www.airportal.cn/",
			privileges: premiumInfo?.airportal?.privileges || [],
		},
		{
			appName: PremiumApp.WEB_HOSTING,
			days: getDays(expirationTime?.rthe?.time),
			explanation: premiumInfo?.rthe?.explanation || "",
			link: "https://host.retiehe.com/",
			privileges: premiumInfo?.rthe?.privileges || [],
		},
	];
	const cardsElem = cards
		.filter((item) => {
			if (serviceParam) {
				return (
					item.appName === SERVICE_PARAM_TO_PREMIUM_APP[serviceParam]
				);
			}
			return true;
		})
		.map((item) => {
			return (
				<PremiumCard
					item={item}
					key={item.appName}
				/>
			);
		});

	const productNameMap = {
		airportal: t("airportal"),
		rthe: t("webHosting"),
	};
	const products = Object.keys(productNameMap).map((item) => {
		return {
			text: productNameMap[item as keyof typeof productNameMap],
			value: item,
		};
	});
	const paymentMethods = [
		{
			icon: faAlipay,
			text: t("alipay"),
			value: PaymentMethod.ALIPAY,
			when: true,
		},
		{
			icon: faWeixin,
			text: t("wechatPay"),
			value: PaymentMethod.WECHAT_PAY,
			when: true,
		},
	];

	useEffect(() => {
		if (!premiumInfo || !product) {
			return;
		}
		const productInfo = getProductInfo(premiumInfo, product);
		if (!productInfo?.price || !("month-1" in productInfo.price)) {
			return;
		}
		const firstPlanPrice = productInfo.price["month-1"];
		if (typeof firstPlanPrice !== "object") {
			return;
		}
		setPlan({
			key: 1,
			originalPrice: firstPlanPrice.original,
			price: firstPlanPrice.special,
		});
	}, [premiumInfo, product]);

	return (
		<>
			<main
				ref={main}
				className="premium"
			>
				<h2>{t("premium")}</h2>
				<div className={styles["premium-cards-container"]}>
					{cardsElem}
				</div>
				{!serviceParam && (
					<section>
						<h4>{t("product")}</h4>
						<Options
							active={product}
							items={products}
							updateActive={handleOptionChange(updateProduct)}
						/>
					</section>
				)}
				{premiumInfo && productInfo && (
					<section>
						<h4>
							<span>{t("plan")}</span>
							{promotion && (
								<span className={styles["promotion"]}>
									<FontAwesomeIcon icon={faTags} />
									{promotion}
								</span>
							)}
						</h4>
						<OptionsPricePremium
							active={plan}
							premiumInfo={premiumInfo}
							service={product}
							updateActive={setPlan}
						/>
					</section>
				)}
				<section>
					<h4>{t("paymentMethod")}</h4>
					<Options
						active={paymentMethod}
						items={paymentMethods}
						updateActive={handleOptionChange(setPaymentMethod)}
					/>
				</section>
				<div className={styles["sticky-button-container"]}>
					<div className={styles["price-info-container"]}>
						<div className={clsx("price", styles["price"])}>
							{CURRENCY}
							{plan.price || "--"}
						</div>
						{plan.originalPrice &&
							plan.price &&
							plan.originalPrice !== plan.price && (
								<div className={styles["price-description"]}>
									{t("saved")} {CURRENCY}
									{plan.originalPrice - plan.price}
								</div>
							)}
					</div>
					<ButtonBlock
						className="place-order"
						onClick={handlePlaceOrderClick}
					>
						{t("placeOrder")}
					</ButtonBlock>
				</div>
				<QrCode
					description={qrCodeDescription}
					qrCode={qrCode}
					show={showCashier}
					onClick={handlePaidClick}
				/>
				{error && (
					<ImportantTip
						text={error.message}
						title={t("error")}
					/>
				)}
				{isLoading && <LoadingScreen />}
			</main>
			<PrivilegesPopup isOpen={signals.arePopupsOpen.value.privileges} />
		</>
	);
}

export default PremiumPage;
